/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import { Logger } from '@quickplay/foundation';
import { AdContentTimePosition } from '@quickplay/player-interface';
import { UserType } from '../../type/UserType';
import { normalizeGodavariPlatform } from '../reporter/godavari/GodavariSdk';
import { parseGodavariNumber } from './GodavariNumberUtil';
import {
  GODAVARI_ATTRIBUTE_VALUES,
  GodavariMetadata,
  GodavariMetadataInput,
  GodavariMetadataStrings,
  GodavariNumericFields,
  GodavariSubscription,
} from '../reporter/model/godavari/GodavariMetadata';
import type { GodavariEventMetadata } from '../reporter/model/godavari/GodavariEventInput';

export const GODAVARI_ENTITLEMENT_FIELDS = [
  'subscribed_pack_name',
  'subscribed_pack_resolution',
  'max_entitled_video_resolution',
] as const satisfies readonly (keyof GodavariMetadataInput)[];

export const GODAVARI_NUMERIC_DEFAULTS = {
  app_user_age: 0,
  normalized_watch_time: 0,
  video_length: 0,
  video_position: 0,
  video_sequence: 0,
  video_session_start_time: 0,
  video_current_bitrate: 0,
  video_session_watch_time_duration: 0,
  video_increment_buffer_time_duration: 0,
  video_monetizable_video_views: 0,
  estimated_bandwidth_in_kbbs: 0,
  buffer_available_in_milliseconds: 0,
  is_multicam_asset: 0,
  video_parent_id: 0,
  video_dvr: 0,
  sponsor_content: 0,
  event_current_playback_rate: 1,
  ads_current_ad_count: 0,
  ads_configured_ad_count: 0,
  ads_pod_position: 0,
  ads_length_watched: 0,
  ads_length_duration: 0,
  ads_current_bitrate: 0,
  ads_pod_ad_position: 0,
  ads_pod_max_ad_count: 0,
  ads_previous_pod: 0,
  ads_ad_buffer_duration: 0,
  ads_watch_time_duration: 0,
  ads_prefetch: 0,
  ad_pod_play_position: 0,
  ads_timestamp_of_adPlay: 0,
} satisfies GodavariNumericFields;

const STRING_FIELDS = {
  app_user_id: true,
  app_device_id: true,
  app_partner_id: true,
  app_version: true,
  app_build_version: true,
  app_platform_type: true,
  app_operating_system: true,
  app_hardware: true,
  app_geo_location: true,
  app_profile_id: true,
  app_gv_device_id: true,
  app_gv_session_id: true,
  app_ppid: true,
  app_sliv_profile_id: true,
  video_session_id: true,
  video_player_name: true,
  video_player_version: true,
  video_codec: true,
  selected_audio_codec: true,
  selected_video_codec: true,
  video_cdn: true,
  video_network_type: true,
  video_connection_speed: true,
  video_session_classifier_experiment_id: true,
  video_connect_time: true,
  video_binge: true,
  video_entry_point: true,
  subscribed_pack_name: true,
  subscribed_pack_resolution: true,
  max_entitled_video_resolution: true,
  initial_video_setting: true,
  updated_video_setting: true,
  video_resolution: true,
  video_starting_bitrate: true,
  video_audio_language: true,
  video_subtitle_language: true,
  video_streaming_host: true,
  video_delivery_protocol: true,
  video_playing_rate: true,
  video_time_to_first_byte: true,
  event_error_message: true,
  ads_session_id: true,
  ads_asset_name: true,
  ads_creative_id: true,
  ads_system: true,
  ads_description: true,
  ads_advertiser: true,
  ads_stream_url: true,
  ads_campaign_id: true,
  ads_advertiser_id: true,
  ads_campaign_name: true,
  ads_technology: true,
  ads_stitcher: true,
  ads_is_slate: true,
  ads_media_file_api_framework: true,
  ads_manager_name: true,
  ads_manager_version: true,
  ads_first_ad_system: true,
  ads_first_ad_id: true,
  ads_first_creative_id: true,
  adend_event_initiator: true,
  vmap_prefetch: true,
} satisfies Record<
  Exclude<
    keyof GodavariMetadataStrings,
    keyof typeof GODAVARI_ATTRIBUTE_VALUES | 'video_content_id' | 'event_error_code' | 'event_exception'
  >,
  true
>;

export function toGodavariSubscription(value: unknown, logger?: Logger): GodavariSubscription | undefined {
  switch (value) {
    case UserType.ANONYMOUS:
    case UserType.REGISTERED:
    case UserType.SUBSCRIBED:
      return value;
    case undefined:
      return undefined;
    default:
      logger?.warn('Godavari cannot map the supplied user type; set app_subscribe_type explicitly.');
      return undefined;
  }
}

export function toGodavariContentId(value: unknown, logger?: Logger): number | undefined {
  if (value === undefined) {
    return undefined;
  }
  const number = typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : value;
  if (typeof number === 'number' && Number.isSafeInteger(number) && number >= 0) {
    return number;
  }
  logger?.warn('Invalid Godavari video_content_id; a nonnegative safe integer is required.');
  return undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function exceptionText(value: unknown, logger?: Logger): string | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (typeof value === 'string') {
    return value;
  }
  if (value instanceof Error) {
    return value.stack || value.message;
  }
  try {
    const text = JSON.stringify(value);
    if (text !== undefined) {
      return text;
    }
  } catch (error) {
    logger?.warn('Godavari exception details could not be serialized.', error);
    return exceptionFallbackText(value);
  }
  logger?.warn('Invalid Godavari exception details; converting to text.');
  return exceptionFallbackText(value);
}

function exceptionFallbackText(value: unknown): string {
  if (typeof value === 'bigint') {
    return value.toString();
  }
  if (typeof value === 'symbol') {
    return value.description || '[Unserializable symbol]';
  }
  if (typeof value === 'function') {
    return value.name ? `[Unserializable function: ${value.name}]` : '[Unserializable function]';
  }
  return '[Unserializable value]';
}

function normalizeStringFields(source: Record<string, unknown>, result: GodavariMetadata, logger?: Logger): void {
  for (const key of Object.keys(STRING_FIELDS)) {
    const value = source[key];
    if (typeof value === 'string') {
      Object.assign(result, { [key]: key === 'app_platform_type' ? normalizeGodavariPlatform(value) : value });
    } else if (value !== undefined) {
      logger?.warn(`Invalid Godavari string field ${key}; ignoring it.`);
    }
  }
}

function normalizeAttributeFields(source: Record<string, unknown>, result: GodavariMetadata, logger?: Logger): void {
  for (const [key, values] of Object.entries(GODAVARI_ATTRIBUTE_VALUES)) {
    const value = source[key];
    if (value === undefined) {
      continue;
    }
    if (typeof value === 'string' && attributeValueIsAccepted(value, values)) {
      Object.assign(result, { [key]: value });
    } else {
      logger?.warn(`Invalid Godavari attribute ${key}; expected ${values.join('|')}.`);
    }
  }
}

function attributeValueIsAccepted(value: string, acceptedValues: readonly string[]): boolean {
  return acceptedValues.includes(value);
}

function normalizeNumericFields(source: Record<string, unknown>, result: GodavariMetadata, logger?: Logger): void {
  for (const [key, fallback] of Object.entries(GODAVARI_NUMERIC_DEFAULTS)) {
    const value = source[key];
    if (value === undefined) {
      continue;
    }
    const number = parseGodavariNumber(value);
    if (number === undefined) {
      logger?.warn(`Invalid Godavari numeric field ${key}; using ${fallback}.`);
    }
    Object.assign(result, { [key]: number ?? fallback });
  }
}

function normalizeContentId(source: Record<string, unknown>, result: GodavariMetadata, logger?: Logger): void {
  const contentId = toGodavariContentId(source.video_content_id, logger);
  if (contentId !== undefined) {
    result.video_content_id = contentId;
  }
}

function normalizeErrorFields(source: Record<string, unknown>, result: GodavariMetadata, logger?: Logger): void {
  const code = source.event_error_code;
  if (typeof code === 'string' || typeof code === 'number') {
    result.event_error_code = String(code);
  } else if (code !== undefined) {
    logger?.warn('Invalid Godavari event_error_code; text or a numeric code is required.');
  }
  result.event_exception = exceptionText(source.event_exception, logger);
}

function normalizeCustomTags(source: Record<string, unknown>, result: GodavariMetadata, logger?: Logger): void {
  if (isRecord(source.custom_tags)) {
    result.custom_tags = source.custom_tags;
  } else if (source.custom_tags !== undefined) {
    logger?.warn('Invalid Godavari custom_tags; an object is required.');
  }
}

function normalizeDroppedFrames(source: Record<string, unknown>, result: GodavariMetadata, logger?: Logger): void {
  const value = source.video_dropped_frames;
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    result.video_dropped_frames = value;
  } else if (value !== undefined) {
    logger?.warn('Invalid Godavari video_dropped_frames; a nonnegative finite number is required.');
  }
}

function normalizeIdentityAliases(source: Record<string, unknown>, result: GodavariMetadata, logger?: Logger): void {
  if (typeof source.user_id === 'string') {
    result.app_user_id = source.user_id;
  }
  if (typeof source.profile_id === 'string') {
    result.app_profile_id = source.profile_id;
  }
  if (source.user_type !== undefined && source.app_subscribe_type === undefined) {
    result.app_subscribe_type = toGodavariSubscription(source.user_type, logger);
  }
}

export function normalizeGodavariMetadata(input: object, logger?: Logger): GodavariMetadata {
  const source: Record<string, unknown> = { ...input };
  const result: GodavariMetadata = {};
  normalizeStringFields(source, result, logger);
  normalizeAttributeFields(source, result, logger);
  normalizeNumericFields(source, result, logger);
  normalizeContentId(source, result, logger);
  normalizeErrorFields(source, result, logger);
  normalizeCustomTags(source, result, logger);
  normalizeDroppedFrames(source, result, logger);
  normalizeIdentityAliases(source, result, logger);
  return Object.fromEntries(Object.entries(result).filter(([, value]) => value !== undefined));
}

function normalizeEventNumbers(source: Record<string, unknown>, result: GodavariEventMetadata, logger?: Logger): void {
  for (const key of [
    'event_start_position',
    'event_end_position',
    'event_duration',
    'current_bitrate',
    'event_estimated_bandwidth',
    'event_available_buffer_duration',
    'event_from_bitrate',
    'event_to_bitrate',
    'event_increment_number',
    'content_playhead_position_ms',
    'sequence',
    'durationMs',
    'duration',
  ] as const) {
    const value = source[key];
    const number = parseGodavariNumber(value);
    if (number !== undefined) {
      Object.assign(result, { [key]: number });
    } else if (value !== undefined) {
      logger?.warn(`Invalid Godavari event field ${key}; a finite number is required.`);
    }
  }
}

function normalizeEventStrings(source: Record<string, unknown>, result: GodavariEventMetadata): void {
  if (typeof source.adSessionId === 'string' && source.adSessionId.trim()) {
    result.adSessionId = source.adSessionId.trim();
  }
  for (const key of ['audio_language', 'subtitle_language', 'adID', 'adId', 'title', 'mediaUrl'] as const) {
    if (typeof source[key] === 'string') {
      Object.assign(result, { [key]: source[key] });
    }
  }
}

function stringOrNumber(value: unknown): string | undefined {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : undefined;
}

function firstString(...values: unknown[]): string | undefined {
  return values.find((value): value is string => typeof value === 'string');
}

function normalizeEventErrors(source: Record<string, unknown>, result: GodavariEventMetadata, logger?: Logger): void {
  result.event_error_code ??= stringOrNumber(source.errorCode) ?? stringOrNumber(source.playback_error_code);
  result.event_error_message ??= firstString(source.errorMessage, source.playback_error_message);
  result.event_exception ??= exceptionText(source.exception ?? source.playback_error_internal, logger);
}

function normalizeVastProperties(
  source: Record<string, unknown>,
  result: GodavariEventMetadata,
  logger?: Logger,
): void {
  if (!isRecord(source.vastProperties)) {
    if (source.vastProperties !== undefined) {
      logger?.warn('Invalid Godavari VAST metadata; an object is required.');
    }
    return;
  }
  const vast = source.vastProperties;
  result.vastProperties = {};
  for (const key of ['adTitle', 'creativeId', 'adSystem', 'description', 'advertiser', 'advertiserId', 'apiFramework'] as const) {
    if (typeof vast[key] === 'string') {
      Object.assign(result.vastProperties, { [key]: vast[key] });
    }
  }
  for (const key of ['wrapperAdIds', 'wrapperAdSystems', 'wrapperCreativeIds'] as const) {
    const values = vast[key];
    if (Array.isArray(values)) {
      Object.assign(result.vastProperties, { [key]: values.filter(item => typeof item === 'string') });
    }
  }
  if (typeof vast.bitrate === 'number' && Number.isFinite(vast.bitrate)) {
    result.vastProperties.bitrate = vast.bitrate;
  } else if (vast.bitrate !== undefined) {
    logger?.warn('Invalid Godavari VAST bitrate; a finite number is required.');
  }
}

function normalizeAdDelivery(source: Record<string, unknown>, result: GodavariEventMetadata): void {
  if (typeof source.isFiller === 'boolean') {
    result.isFiller = source.isFiller;
  }
  if (!isRecord(source.delivery)) {
    return;
  }
  const delivery = source.delivery;
  result.delivery = {};
  for (const key of ['stitcher', 'managerName', 'managerVersion', 'adTagUrl'] as const) {
    if (typeof delivery[key] === 'string') {
      Object.assign(result.delivery, { [key]: delivery[key] });
    }
  }
  if (delivery.technology === 'client-side' || delivery.technology === 'server-side') {
    result.delivery.technology = delivery.technology;
  }
}

function normalizeAdBreak(
  key: 'adBreakInfo' | 'adBreak',
  source: Record<string, unknown>,
  result: GodavariEventMetadata,
  logger?: Logger,
): void {
  const adBreak = source[key];
  if (!isRecord(adBreak)) {
    if (adBreak !== undefined) {
      logger?.warn(`Invalid Godavari ${key}; an object is required.`);
    }
    return;
  }
  const position = adBreak.contentTimePosition ?? adBreak.position;
  const totalAds =
    typeof adBreak.totalAds === 'number' && Number.isFinite(adBreak.totalAds) ? adBreak.totalAds : undefined;
  const contentTimePosition = Object.values(AdContentTimePosition).find(value => value === position);
  if (position !== undefined && contentTimePosition === undefined) {
    logger?.warn(`Invalid Godavari ${key} position; a player ad position is required.`);
  }
  if (adBreak.totalAds !== undefined && totalAds === undefined) {
    logger?.warn(`Invalid Godavari ${key} totalAds; a finite number is required.`);
  }
  if (key === 'adBreakInfo') {
    const adBreakIndex =
      typeof adBreak.adBreakIndex === 'number' && Number.isFinite(adBreak.adBreakIndex)
        ? adBreak.adBreakIndex
        : undefined;
    result.adBreakInfo = { contentTimePosition, totalAds, adBreakIndex };
  } else {
    result.adBreak = { position: contentTimePosition, totalAds };
  }
}

function normalizeAdBreaks(source: Record<string, unknown>, result: GodavariEventMetadata, logger?: Logger): void {
  for (const key of ['adBreakInfo', 'adBreak'] as const) {
    normalizeAdBreak(key, source, result, logger);
  }
}

export function normalizeGodavariEvent(input: object, logger?: Logger): GodavariEventMetadata {
  const source: Record<string, unknown> = { ...input };
  const result: GodavariEventMetadata = normalizeGodavariMetadata(input, logger);
  normalizeEventNumbers(source, result, logger);
  normalizeEventStrings(source, result);
  normalizeEventErrors(source, result, logger);
  normalizeVastProperties(source, result, logger);
  normalizeAdBreaks(source, result, logger);
  normalizeAdDelivery(source, result);
  return Object.fromEntries(Object.entries(result).filter(([, value]) => value !== undefined));
}

/** Identity removals depend on raw field presence, which normalization otherwise discards. */
export interface GodavariIdentityUpdate {
  removeUser: boolean;
  removeProfile: boolean;
  clearSubscription: boolean;
}

export function getGodavariIdentityUpdate(input: object): GodavariIdentityUpdate {
  return {
    removeUser: 'user_id' in input && input.user_id === undefined,
    removeProfile: 'profile_id' in input && input.profile_id === undefined,
    clearSubscription: 'user_type' in input,
  };
}

const METADATA_FIELDS = new Set([
  ...Object.keys(STRING_FIELDS),
  ...Object.keys(GODAVARI_ATTRIBUTE_VALUES),
  ...Object.keys(GODAVARI_NUMERIC_DEFAULTS),
  'video_content_id',
  'event_error_code',
  'event_exception',
  'custom_tags',
  'video_dropped_frames',
]);

/** Project validated event fields without validating them again or retaining player-only fields. */
export function pickGodavariMetadata(event: GodavariEventMetadata): GodavariMetadata {
  return Object.fromEntries(
    Object.entries(event).filter(([key, value]) => METADATA_FIELDS.has(key) && value !== undefined),
  );
}
